/**
 * Security settings (auth phone number + SMS 2FA) — calls the ECS API server.
 *
 * These used to be `cognito-idp` `admin*` calls made straight from the
 * browser, which required pool-wide Cognito permissions on the caller's
 * identity-pool role and so could only ever be granted to Admins. The
 * api-server holds that grant now and authorizes per target: any internal
 * user may manage their own settings, an Admin may manage anyone's.
 */
import { createApiClient } from "~/api/apiClient";

export interface UserSecuritySettings {
  phoneNumber: string;
  phoneNumberVerified: boolean;
  smsMfaEnabled: boolean;
}

// Cognito stores phone_number in E.164 (+1XXXXXXXXXX). The shared
// FormattedTextAreaBlock stores/handles 10 digits. Convert for display;
// the API server normalizes whatever we send back to E.164.
export const fromE164 = (e164: string): string => {
  if (!e164) return "";
  const digits = e164.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  return digits;
};

export const isCompletePhone = (digits: string): boolean =>
  digits.replace(/\D/g, "").length >= 10;

/** Message from a 422 (bad phone number, or 2FA with no number to send to). */
const validationMessage = (error: unknown): string | null => {
  const detail = (error as { detail?: unknown })?.detail;
  return typeof detail === "string" ? detail : null;
};

export const getUserSecurity = async (
  cognitoSub: string,
): Promise<UserSecuritySettings> => {
  const client = createApiClient();
  const { data, error } = await client.GET("/user-security/{cognito_sub}", {
    params: { path: { cognito_sub: cognitoSub } },
  });
  if (error || !data) {
    throw new Error(
      validationMessage(error) ?? "Failed to load security settings",
    );
  }
  return {
    phoneNumber: data.phone_number,
    phoneNumberVerified: data.phone_number_verified,
    smsMfaEnabled: data.sms_mfa_enabled,
  };
};

/**
 * Set the phone number and/or SMS 2FA state as one unit. The server
 * normalizes the number to E.164 and orders the two Cognito writes so a
 * number is never removed out from under an enabled 2FA setting, so the
 * caller just states the desired end state. Pass an empty `phoneNumber`
 * to clear it.
 */
export const updateUserSecurity = async (
  cognitoSub: string,
  phoneNumber: string,
  smsMfaEnabled: boolean,
): Promise<UserSecuritySettings> => {
  const client = createApiClient();
  const { data, error } = await client.PUT("/user-security/{cognito_sub}", {
    params: { path: { cognito_sub: cognitoSub } },
    body: { phone_number: phoneNumber, sms_mfa_enabled: smsMfaEnabled },
  });
  if (error || !data) {
    throw new Error(validationMessage(error) ?? "Failed to save changes");
  }
  return {
    phoneNumber: data.phone_number,
    phoneNumberVerified: data.phone_number_verified,
    smsMfaEnabled: data.sms_mfa_enabled,
  };
};
